import { useEffect, useState, type ReactNode } from "react";
import type { LeadCardResponse, LeadRow } from "@gioia365/contracts";
import { usePrefetchInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ApiRequestError } from "../../api/http";
import { useTab } from "../../api/queries";
import { useDialerCard } from "../../dialer/dialer-session";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { Icon } from "../../design/components/Icon";
import { PageHeader } from "../../design/components/PageHeader";
import { Reason } from "../../design/components/Reason";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import { OpportunityDialog } from "../../opportunities/OpportunityDialog";
import { opportunityScopeOf, useAccess } from "../../session/access";
import { tabKey } from "../../session/tab-context";
import { useCallFromTasks } from "../../tasks/call-from-tasks";
import { tasksSearchFromSource, timelineItemOfParam, type TasksSearch } from "../../tasks/task-search";
import { forgetLead } from "../archive/archive-queries";
import { leadName, statusTone } from "../lead-display";
import { cachedLeadRow, leadCardQuery, leadKeys, SupersededWriteError, timelineQuery, useLeadCardWrites, useLeadView } from "../lead-queries";
import { callAnchorId, timelineAnchorId } from "../timeline/focus";
import { Timeline } from "../timeline/Timeline";
import { CallDesk } from "./CallDesk";
import { NOTE_TARGET_ID } from "./card-fields";
import { DuplicateNotice } from "./DuplicateNotice";
import { LeadDataBlock } from "./LeadDataBlock";
import { cardTitle, LeadHeader } from "./LeadHeader";
import { LeadSummary } from "./LeadSummary";
import { SequenceBlock } from "./SequenceBlock";
import "../lead-list.css";
import "./lead-card.css";

// La scheda Lead (piano M2 Task 18, mock-up `scheda-lead.html`, rotta `/lead/$leadId`): testata, poi tre aree, i dati a
// sinistra (dal modello pubblicato in «Campi della scheda»), la Timeline al centro (Task 19, letta insieme alla scheda
// e mai in attesa di lei) e il «Riepilogo» a destra;
// sopra i 1280 px 300 px | resto | 260 px, due colonne fino ai 720 px, una sotto (Timeline, riepilogo, dati: l'ordine
// operativo del mock-up). Si apre subito dall'elenco: il nome sopra un Lead la legge in anticipo, e mentre arriva la
// testata si disegna con la riga dell'elenco. Ogni modifica si salva sul posto, ottimistica, con la versione letta.
// Dopo «Archivia» (piano M3 Task 12) si torna all'elenco con l'avviso, e la scheda non resta in cache. La «Postazione di
// chiamata» (piano M4, Task 13) sta sopra la Timeline, nella colonna centrale; in una colonna sola, in cima alla scheda.
// Con una sessione del Power Dialer (piano M5, Task 5) la scheda del Lead aperto si registra nel provider del guscio: la
// chiamata della cascata parte da lì, con la sessione, sul primo telefono che si chiama e con il nome della scheda; «Lead
// successivo» sta soltanto nella barra in alto, la postazione dice quando i passi sono fatti. La sezione «Sequenza di chiamate»
// (piano M5, Task 12) sta sotto la testata e sopra le tre aree (e quindi sopra la postazione): «Vedi nella Timeline» di una sua
// casella porta in vista la telefonata nella Timeline. Aperta da «Le cose da fare» (piano M5, Task 18), in alto c'è «Torna ai
// Task», alla stessa scheda e alla stessa persona, al posto di «Torna all'elenco»; con «Chiama» da una riga dei Task la chiamata
// parte appena la scheda c'è (`useCallFromTasks`, dallo stato della cronologia e mai dall'indirizzo: ruling Q7 e Q57). Sotto la testata,
// prima della «Sequenza di chiamate», i possibili doppioni del Lead (LEAD-28, `DuplicateNotice`). La finestra «Opportunità di …»
// (LEAD-2, `OpportunityDialog.tsx`) sta qui, perché la aprono «Opportunità» in testata e «Apri l'opportunità» del riquadro; con
// `?opportunita=1` (il dettaglio della pagina «Opportunità») si apre una volta all'arrivo, e il parametro esce dall'indirizzo
// (`replace`), così tornando indietro o ricaricando non si riapre. Senza l'area `opportunities` non si apre.

export function LeadCardPage() {
  const { leadId } = useParams({ from: "/autenticato/guscio/azienda/lead/$leadId" });
  const { da, opportunita, evento } = useSearch({ from: "/autenticato/guscio/azienda/lead/$leadId" });
  // Un altro Lead è un'altra scheda: niente avvisi, caselle aperte o scritture in coda di quello di prima.
  return <LeadCard key={leadId} leadId={leadId} fromTasks={tasksSearchFromSource(da)} openOpportunity={opportunita === 1} shownAtArrival={evento ?? null} />;
}

type LeadCardProps = { leadId: string; fromTasks: TasksSearch | null; openOpportunity: boolean; shownAtArrival: string | null };

function LeadCard({ leadId, fromTasks, openOpportunity, shownAtArrival }: LeadCardProps) {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const view = useLeadView();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const card = useQuery(leadCardQuery(tab, leadId, view));
  // La prima pagina della Timeline parte insieme alla scheda, non dopo: quando la scheda arriva, la Timeline c'è già.
  usePrefetchInfiniteQuery(timelineQuery(tab, leadId, "all", "", view));
  const writes = useLeadCardWrites(tab, leadId, view);
  useDialerCard(tab, card.data ?? null, card.data ? cardTitle(t, card.data.lead) : null);
  useCallFromTasks(tab, leadId, card.data ?? null, card.data ? cardTitle(t, card.data.lead) : null);
  // Un rifiuto della scheda intera (qualcun altro l'ha cambiata, i permessi o il modello sono cambiati): si dice in cima,
  // finché la scheda resta quella riletta allora; il primo salvataggio riuscito dopo (versione nuova) lo toglie.
  const [cardError, setCardError] = useState<{ error: unknown; version: number | undefined } | null>(null);
  // «Vedi nella Timeline» (piano M5, Task 12): la telefonata da portare in vista; ogni richiesta è un oggetto nuovo, anche per
  // la stessa telefonata. Con `?evento=<tipo>:<id>` (il link di una riga di «Attività», SEZ-8) l'elemento da portare in vista
  // all'arrivo; il parametro esce subito dall'indirizzo, come `opportunita`, così tornando indietro o ricaricando non si sposta di nuovo la pagina.
  const [shownItem, setShownItem] = useState<{ anchor: string } | null>(() => {
    const item = timelineItemOfParam(shownAtArrival);
    return item === null ? null : { anchor: timelineAnchorId(item.kind, item.id) };
  });
  useEffect(() => {
    if (shownAtArrival === null) return;
    void navigate({ to: "/lead/$leadId", params: { leadId }, search: (previous) => (previous.da === undefined ? {} : { da: previous.da }), replace: true });
  }, [shownAtArrival, navigate, leadId]);
  // La finestra «Opportunità di …»: aperta dalla testata, dal riquadro o, una volta all'arrivo, dall'indirizzo.
  const canSeeOpportunities = opportunityScopeOf(access, "view") !== "none";
  const [opportunityOpen, setOpportunityOpen] = useState(openOpportunity && canSeeOpportunities);
  useEffect(() => {
    if (!openOpportunity) return;
    void navigate({ to: "/lead/$leadId", params: { leadId }, search: (previous) => (previous.da === undefined ? {} : { da: previous.da }), replace: true });
  }, [openOpportunity, navigate, leadId]);
  const organization = access.context.organization.name;
  const back = fromTasks ? (
    <Link to="/task" search={fromTasks} className="g-button g-lead-card__back" data-variant="ghost">
      {t("tasks.backToTasks")}
    </Link>
  ) : (
    <Link to="/lead" className="g-button g-lead-card__back" data-variant="ghost">
      {t("leadCard.backToList")}
    </Link>
  );

  // Il Lead archiviato non ha più una scheda: si torna all'elenco, e soltanto dopo, a scheda chiusa (aperta, la
  // rileggerebbe), la sua scheda e la sua Timeline escono dalla cache.
  function afterArchive() {
    void navigate({ to: "/lead" }).then(() => forgetLead(queryClient, tab, leadId));
    toast.show(t("leadArchive.done"), "success");
  }

  function reportCardError(error: unknown) {
    if (error instanceof SupersededWriteError) return;
    const version = queryClient.getQueryData<LeadCardResponse>(leadKeys.card(tabKey(tab), leadId, view))?.lead.version;
    setCardError({ error, version });
  }

  // Un rifiuto definitivo della rilettura (il Lead non c'è più per chi guarda: riassegnato, «Chi vede i Lead» cambiato) vale anche
  // con la scheda di prima in cache, che TanStack Query tiene: la scheda non resta con i suoi pulsanti (rilievo di Codex sulla #55,
  // turno di notte del 10 ottobre 2026).
  const gone = card.isError && card.error instanceof ApiRequestError && card.error.status === 404;
  if (!card.data || gone) {
    if (card.isError) {
      return (
        <div className="g-stack">
          <PageHeader eyebrow={organization} title={t("shell.nav.leads")} actions={back} />
          <Reason tone="warning" role="alert">{messageForError(t, card.error)}</Reason>
          <div>
            <Button onClick={() => void card.refetch()}>{t("common.retry")}</Button>
          </div>
        </div>
      );
    }
    return <CardPlaceholder row={cachedLeadRow(queryClient, tabKey(tab), leadId)} back={back} />;
  }

  const data = card.data;
  const { permissions, ownerNotice } = data;
  const shownError = cardError && cardError.version === data.lead.version ? cardError.error : null;
  const cardErrorText =
    shownError === null ? null : shownError instanceof ApiRequestError && shownError.code === "version_conflict" ? t("leadCard.conflict") : messageForError(t, shownError);

  function goToNote() {
    const target = document.getElementById(NOTE_TARGET_ID);
    if (!target) return;
    if (typeof target.scrollIntoView === "function") target.scrollIntoView({ block: "center", behavior: "smooth" });
    target.focus({ preventScroll: true });
  }

  return (
    <div className="g-lead-card">
      <div className="g-lead-card__top">{back}</div>
      {cardErrorText ? (
        <Reason tone="warning" role="alert" className="g-lead-card__notice">
          {cardErrorText}
        </Reason>
      ) : null}
      {ownerNotice ? (
        <Reason className="g-lead-card__notice">
          {permissions.canUpdate ? t("leadCard.colleague", { name: ownerNotice.ownerDisplayName }) : t("leadCard.colleagueReadOnly", { name: ownerNotice.ownerDisplayName })}
        </Reason>
      ) : !permissions.canUpdate ? (
        <Reason className="g-lead-card__notice">{t("leadCard.readOnly")}</Reason>
      ) : null}
      <LeadHeader
        tab={tab}
        card={data}
        writes={writes}
        onCardError={reportCardError}
        onNote={goToNote}
        onOpportunity={() => setOpportunityOpen(true)}
        onArchived={afterArchive}
      />
      <DuplicateNotice tab={tab} card={data} view={view} timeZone={access.context.settings.timeZone} />
      <SequenceBlock
        tab={tab}
        leadId={leadId}
        view={view}
        sequence={data.sequence}
        canStart={permissions.canUpdate && view === null}
        timeZone={access.context.settings.timeZone}
        onShowCall={(callId) => setShownItem({ anchor: callAnchorId(callId) })}
      />
      <div className="g-lead-card__grid">
        <LeadDataBlock tab={tab} card={data} writes={writes} onCardError={reportCardError} />
        <div className="g-lead-card__center">
          <CallDesk tab={tab} card={data} leadName={cardTitle(t, data.lead)} writes={writes} onCardError={reportCardError} timeZone={access.context.settings.timeZone} />
          <Timeline
            tab={tab}
            leadId={leadId}
            view={view}
            card={data}
            timeZone={access.context.settings.timeZone}
            shownItem={shownItem}
            onItemShown={() => setShownItem(null)}
          />
        </div>
        <LeadSummary tab={tab} card={data} timeZone={access.context.settings.timeZone} onOpenOpportunity={() => setOpportunityOpen(true)} />
      </div>
      {opportunityOpen && canSeeOpportunities ? (
        <OpportunityDialog
          tab={tab}
          card={data}
          leadName={cardTitle(t, data.lead)}
          timeZone={access.context.settings.timeZone}
          onClose={() => setOpportunityOpen(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * La scheda mentre arriva: la testata con ciò che l'elenco sa già del Lead (nome, salone, owner, status, tag), e il posto
 * delle tre aree, così la struttura non salta quando arrivano i dati.
 */
function CardPlaceholder({ row, back }: { row: LeadRow | undefined; back: ReactNode }) {
  const t = useT();
  return (
    <div className="g-lead-card" aria-busy="true">
      <div className="g-lead-card__top">{back}</div>
      <header className="g-lead-head">
        <div className="g-lead-head__identity">
          <p className="g-lead-head__eyebrow">{t("leadCard.eyebrow")}</p>
          <h1 className="g-lead-head__title">{row ? (leadName(row) ?? "—") : t("leadCard.loadingCard")}</h1>
          {row ? (
            <>
              <p className="g-lead-head__facts">
                {row.salonName ? <strong>{row.salonName}</strong> : null}
                <span className="g-lead-head__fact">
                  <Icon name="user" />
                  {t("leadCard.ownerFact", { name: row.owner?.displayName ?? t("leadCard.noOwner") })}
                </span>
              </p>
              <div className="g-lead-head__facts">
                <span className="g-lead-table__status" data-tone={statusTone(row.status.name)}>
                  {row.status.name}
                </span>
                {row.tags.length > 0 ? (
                  <span className="g-lead-head__tags">
                    {row.tags.map((tag) => (
                      <Chip key={tag.id} icon="tag">
                        {tag.name}
                      </Chip>
                    ))}
                  </span>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </header>
      <p className="g-visually-hidden" role="status">
        {t("leadCard.loadingCard")}
      </p>
      <div className="g-lead-card__grid" aria-hidden="true">
        <div className="g-lead-card__skeleton" data-area="data" />
        <div className="g-lead-card__skeleton" data-area="timeline" />
        <div className="g-lead-card__skeleton" data-area="summary" />
      </div>
    </div>
  );
}
